// @architect-approved: INFRASTRUCTURE - Loader is not a DB-registered component (no @tg-seq), does not follow Self/getTsxFileName identity pattern (same exemption class as App.tsx)
/**
 * DefaultLandingLoader.tsx — Database-Driven Front Door
 *
 * PURPOSE: Resolves which dirm is the app landing page (path="/")
 * from the database, then resolves + lazy-loads it via the manifest-driven
 * resolveComponentBySeq() path.
 *
 * ARCHITECTURE:
 *   - Calls apiRequestBySeq(10368, 1, { manage: 8 }) (GET_APP_DEFAULT)
 *     (was hardcoded POST /api/directory-registry-v2 — dead path after the
 *     Aug 2026 sequence-based routing migration; router now lives at
 *     /api/tsx/10368/1, see Router_Registry_ID 990379)
 *   - Gets the Sequence_Num of the dirm with Is_App_Default = 1
 *   - Resolves + lazy-loads the component via resolveComponentBySeq() (the
 *     same manifest-driven resolution path every other route/tab in the app
 *     uses) — never string-matches Component_TSXFileName against a glob key
 *   - Caches the result — only one API call per session
 *
 * WHY THIS EXISTS:
 *   - App.tsx becomes truly immutable (no directory imports)
 *   - Change the landing page = one database UPDATE, zero code changes
 *   - Rename any dirm file = App.tsx untouched, hash unchanged
 *   - The "front door from the street" is a database decision
 *
 * FALLBACK (2026-09-30): If no default is set, the user has no access token
 * yet (public visitor), or the live DB lookup fails, resolves the app default
 * from the LOCAL directory-navigation-manifest.json's `appDefault` field
 * instead of a hardcoded static component import. That manifest is the same
 * build-time-synced source navigateToAppDefault() (directoryNavigation.ts)
 * reads, and resolution is synchronous (no network call) via
 * resolveComponentBySeq() - so renaming/re-sequencing the front-door
 * component can never silently break this loader.
 *
 * Created: 2026-03-21
 */

import { Box, CircularProgress, Typography } from '@mui/material';
import React, { Suspense, useEffect, useState } from 'react';
import directoryManifest from '../../directory-navigation-manifest.json';
import { apiRequestBySeq } from '../../utils/apiService';
import { resolveComponentBySeq } from '../../utils/componentResolver';
import { SecureStorage } from '../../utils/SecureStorage';

// ============================================================
// Cache — one API call per session
// ============================================================
let cachedComponent: React.LazyExoticComponent<React.ComponentType<any>> | null = null;
let cachedError: string | null = null;
let resolvePromise: Promise<void> | null = null;

/**
 * Resolve the app default directory's host component from the LOCAL manifest
 * (directory-navigation-manifest.json `appDefault` -> directories[...].componentSequenceNum),
 * synchronously, with no API call. Used for the public/unauthenticated fast
 * path and as the last-resort fallback when the live DB lookup fails.
 */
function resolveAppDefaultFromManifest(): React.LazyExoticComponent<
    React.ComponentType<any>
> | null {
    try {
        const manifest = directoryManifest as any;
        const directory = manifest.directories?.[manifest.appDefault];
        const componentSeq = directory?.componentSequenceNum;
        if (!componentSeq) {
            console.error(
                `[DefaultLandingLoader] appDefault "${manifest.appDefault}" has no componentSequenceNum in directory-navigation-manifest.json. Run: npm run sync-manifests`,
            );
            return null;
        }
        return resolveComponentBySeq(componentSeq);
    } catch (error) {
        console.error('[DefaultLandingLoader] Manifest-driven fallback resolution failed:', error);
        return null;
    }
}

/**
 * Resolve the default landing component from the database
 * Falls back to /welcome if no default configured OR user is not authenticated
 *
 * CRITICAL: Floor 0 (welcome page) is PUBLIC - no authentication required.
 * Only make API call if user has an access token (personalized landing).
 */
async function resolveDefaultLanding(): Promise<void> {
    if (cachedComponent || cachedError) return;

    // ⭐ Check authentication status FIRST
    const accessToken = SecureStorage.getAccessToken();
    if (!accessToken) {
        console.log(
            '[DefaultLandingLoader] No access token - resolving public front door from manifest appDefault (Floor 0)',
        );
        cachedComponent = resolveAppDefaultFromManifest();
        if (!cachedComponent) {
            cachedError = 'No default configured, redirecting to /welcome';
        }
        return;
    }

    try {
        const response: any = await apiRequestBySeq(10368, 1, {
            manage: 8, // GET_APP_DEFAULT (SP @Manage 8 — frontend 1 now maps to MANIFEST/60 per 2026-07-22 catalog realignment)
        });

        if (!response?.success || !response?.data) {
            cachedError = response?.warning || 'No default directory configured';
            return;
        }

        // FIX (2026-09-29): resolve by Sequence_Num (the Component_Sequence_Num
        // of the app-default directory) through the SAME canonical resolution
        // path every other route/tab in the app uses (resolveComponentBySeq -
        // manifest lookup + glob registry), instead of hand-matching the raw
        // Component_TSXFileName string against a glob-key. That string is a
        // DB-computed value that embeds the security "tumbler" cypher segment
        // (e.g. "_T1100000_") and is NOT the bare physical file name - matching
        // against it directly broke the moment the tumbler value didn't match
        // the on-disk file name. The URL/route only ever needs prefix, domain
        // code, sequence, and friendly-name segments; the manifest
        // (tsx-inventory-manifest.json, via resolveComponentBySeq) is the
        // single source of truth for the actual physical file path - not this
        // loader re-deriving it from a filename string.
        const sequenceNum = response.data.sequence_num;
        if (!sequenceNum) {
            cachedError = 'Default directory has no Sequence_Num in Component_Registry2';
            return;
        }

        cachedComponent = resolveComponentBySeq(sequenceNum);
    } catch (error) {
        console.error('[DefaultLandingLoader] Failed to resolve default landing:', error);
        cachedError = 'Failed to connect to directory service';
    }
}

// ============================================================
// Loading fallback
// ============================================================
const LoadingFallback: React.FC = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            flexDirection: 'column',
            gap: 2,
        }}
    >
        <CircularProgress size={48} />
        <Typography
            variant="body2"
            sx={{
                color: 'text.secondary',
            }}
        >
            Opening the front door...
        </Typography>
    </Box>
);

// ============================================================
// Main Component
// ============================================================
const DefaultLandingLoader: React.FC = () => {
    const [loading, setLoading] = useState(!cachedComponent && !cachedError);
    const [error, setError] = useState<string | null>(cachedError);

    useEffect(() => {
        // CRITICAL: Only run this logic when actually on "/" route
        // Don't hijack navigation when user has already moved to another page
        const isOnRootRoute = window.location.pathname === '/';

        if (!isOnRootRoute) {
            console.log('[DefaultLandingLoader] Not on root route, skipping default resolution');
            return;
        }

        if (cachedComponent || cachedError) {
            setLoading(false);
            setError(cachedError);
            // On error, the render below falls back to the manifest-driven
            // app default - no manual button/redirect needed.
            return;
        }

        // Only one resolve in-flight at a time
        if (!resolvePromise) {
            resolvePromise = resolveDefaultLanding();
        }

        resolvePromise.then(() => {
            setLoading(false);
            setError(cachedError);
            resolvePromise = null;
            // On error, the render below falls back to the manifest-driven
            // app default - no manual button/redirect needed.
        });
    }, []);

    if (loading) {
        return <LoadingFallback />;
    }

    if (error || !cachedComponent) {
        // FIX (2026-09-29, revised 2026-09-30): the DB-driven resolution
        // (Is_App_Default lookup + glob-key filename match) can fail for
        // reasons unrelated to whether a working front door actually exists
        // (API error, stale filename in Component_Registry2, etc.) - showing
        // the user a dead-end error page in that case is wrong. Fall back to
        // resolving the SAME appDefault directory from the local manifest
        // (synchronous, no API call) instead of a hardcoded static import -
        // this can never go stale the way a hand-written import path can.
        console.error('[DefaultLandingLoader] Falling back to manifest-driven app default:', error);
        const FallbackComponent = resolveAppDefaultFromManifest();
        if (!FallbackComponent) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '60vh',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Unable to load the front door. Please refresh the page.
                    </Typography>
                </Box>
            );
        }
        return (
            <Suspense fallback={<LoadingFallback />}>
                <FallbackComponent />
            </Suspense>
        );
    }

    const DefaultComponent = cachedComponent;
    return (
        <Suspense fallback={<LoadingFallback />}>
            <DefaultComponent />
        </Suspense>
    );
};

export default DefaultLandingLoader;
